import { createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";

export const sanityClient = createClient({
  projectId: "fs0iei0r",
  dataset: "production",
  useCdn: true,
  apiVersion: "2025-01-01",
});

// Create image URL builder
const builder = imageUrlBuilder(sanityClient);

// Helper function to build image URLs
const urlFor = (source) => builder.image(source);

// Query to fetch all blog posts ordered by publishedAt descending
export const postsQuery = `
  *[_type == "post"] | order(publishedAt desc) {
    _id,
    title,
    slug,
    author,
    publishedAt,
    mainImage{
      asset->{
        _id,
        url
      }
    },
    body
  }
`;

// Query to fetch a single post by slug with full content
export const postDetailQuery = (slug) => `
  *[_type == "post" && slug.current == "${slug}"] {
    _id,
    title,
    slug,
    author,
    publishedAt,
    mainImage{
      asset->{
        _id,
        url
      }
    },
    body
  }[0]
`;

// Fetch all posts
// Results are cached for the session so pages can open instantly: a card hover/touch
// prefetches its post, and /blog reuses the list the home page already loaded.
// `peek*` return the value synchronously once it has arrived (null before that).
const postCache = new Map();
const resolved = new Map();
let postsPromise = null;
let postsValue = null;

// The server-rendered blog pages (api/blog/*) seed their data so React paints on the first frame
if (typeof window !== "undefined") {
  if (Array.isArray(window.__SANITY_POSTS__)) {
    postsValue = window.__SANITY_POSTS__;
    postsPromise = Promise.resolve(postsValue);
  }
  const seeded = window.__SANITY_POST__;
  if (seeded?.slug?.current) {
    resolved.set(seeded.slug.current, seeded);
    postCache.set(seeded.slug.current, Promise.resolve(seeded));
  }
}

export const fetchPosts = () => {
  postsPromise ??= sanityClient.fetch(postsQuery).then(
    (posts) => (postsValue = posts),
    (error) => {
      postsPromise = null; // allow a retry
      throw error;
    }
  );
  return postsPromise;
};
export const peekPosts = () => postsValue;

export const fetchPostBySlug = (slug) => {
  if (!postCache.has(slug)) {
    postCache.set(
      slug,
      sanityClient.fetch(postDetailQuery(slug)).then(
        (post) => {
          resolved.set(slug, post);
          return post;
        },
        (error) => {
          postCache.delete(slug);
          throw error;
        }
      )
    );
  }
  return postCache.get(slug);
};
export const peekPost = (slug) => resolved.get(slug) ?? null;

// Helper to generate image URL from Sanity image using URL builder
export const getImageUrl = (imageAsset) => {
  if (!imageAsset) return null;

  try {
    // Handle different image asset structures
    let imageToBuild = imageAsset;
    
    // If the image has an asset property with url, use it directly
    if (imageAsset.asset && imageAsset.asset.url) {
      return imageAsset.asset.url;
    }
    
    // Otherwise, use Sanity's image URL builder
    return urlFor(imageToBuild).width(800).fit("max").auto("format").url();
  } catch (error) {
    return null;
  }
};

// Helper to format date
export const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};
